import { useEffect, useState, useCallback } from "react";
import { useParams, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import UserAvatar from "@/components/UserAvatar";
import { toast } from "sonner";
import { Buildings, Users, ChartLine, CurrencyDollar, CreditCard, Trophy, Sword, ShieldStar, UserPlus, LockKey, MagnifyingGlass } from "@phosphor-icons/react";

const fmtDuration = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const fmtMxn = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;

export default function ClubDashboard() {
  const { user } = useAuth();
  const { clubId } = useParams();
  const [access, setAccess] = useState(null);
  const [data, setData] = useState(null);
  const [managersOpen, setManagersOpen] = useState(false);

  const loadAccess = useCallback(async () => {
    try {
      const { data } = await api.get(`/clubs/${clubId}/my-access`);
      setAccess(data);
    } catch { setAccess({ is_manager: false }); }
  }, [clubId]);

  const loadDashboard = useCallback(async () => {
    try {
      const { data } = await api.get(`/clubs/${clubId}/dashboard`);
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Sin acceso"); }
  }, [clubId]);

  useEffect(() => { loadAccess(); }, [loadAccess]);
  useEffect(() => { if (access?.is_manager) loadDashboard(); }, [access, loadDashboard]);

  if (access === null) return <div className="p-8 text-gray-400 text-center">Verificando acceso…</div>;
  if (!access.is_manager) return <Navigate to="/dashboard" replace />;
  if (!data) return <div className="p-8 text-gray-400 text-center">Cargando dashboard…</div>;

  const k = data.kpis || {};
  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6" data-testid="club-dashboard-page">
      <header className="flex items-center gap-3 flex-wrap">
        <Buildings size={36} weight="fill" className="text-[#CCFF00]" />
        <div className="flex-1">
          <p className="text-[10px] tracking-widest uppercase text-[#CCFF00]">Panel del Club</p>
          <h1 className="text-2xl md:text-3xl font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white">{data.club?.name || "Club"}</h1>
        </div>
        {isAdmin && (
          <Button onClick={() => setManagersOpen(true)} className="bg-[#CCFF00] text-black font-bold text-xs" data-testid="club-managers-btn">
            <ShieldStar size={14} className="mr-1" /> Autorizados
          </Button>
        )}
      </header>

      {/* KPIs */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="club-kpis">
        <Kpi icon={Users} label="Miembros" value={k.members} />
        <Kpi icon={ChartLine} label="Activos 30d" value={k.active_users_30d} sub={`${k.sessions_30d || 0} sesiones`} />
        <Kpi icon={CurrencyDollar} label="Ingresos 90d" value={fmtMxn(k.income_total_90d)} accent />
        <Kpi icon={Trophy} label="Actividad" value={`${k.matches || 0}m · ${k.tournaments || 0}t · ${k.retas || 0}r`} sub="matches · torneos · retas" />
      </section>

      {/* Pagos */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white/5 border border-white/10 rounded p-4" data-testid="club-payments-stripe">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-400 mb-2"><CreditCard size={14} className="text-[#CCFF00]" /> Stripe (últimos 90d)</div>
          <p className="text-3xl font-['Barlow_Condensed'] font-black text-white">{fmtMxn(k.income_stripe_90d)}</p>
          <p className="text-xs text-gray-500">{k.payments_stripe_90d || 0} transacciones</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded p-4" data-testid="club-payments-transfer">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-400 mb-2"><CurrencyDollar size={14} className="text-[#CCFF00]" /> Transferencia (últimos 90d)</div>
          <p className="text-3xl font-['Barlow_Condensed'] font-black text-white">{fmtMxn(k.income_transfer_90d)}</p>
          <p className="text-xs text-gray-500">{k.payments_transfer_90d || 0} transacciones</p>
        </div>
      </section>

      {/* Top users */}
      <section className="bg-white/5 border border-white/10 rounded p-4" data-testid="club-top-users">
        <div className="flex items-center gap-2 mb-3">
          <ChartLine size={18} className="text-[#CCFF00]" />
          <h2 className="text-lg font-['Barlow_Condensed'] font-bold uppercase text-white">Top usuarios por uso (30d)</h2>
        </div>
        <ul className="space-y-1.5">
          {(data.top_users || []).map((u, i) => (
            <li key={u.id} className="flex items-center gap-3 p-2 bg-black/30 border border-white/5 rounded" data-testid={`club-top-user-${u.id}`}>
              <span className={`w-7 h-7 flex items-center justify-center rounded-full font-bold text-xs shrink-0 ${i < 3 ? "bg-[#CCFF00] text-black" : "bg-white/10 text-gray-300"}`}>{i + 1}</span>
              <UserAvatar src={u.profile_photo_url} fallback={(u.name || "?").slice(0, 2)} className="w-9 h-9 rounded-full" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white truncate">{u.name || "Usuario"}</p>
                <p className="text-[10px] text-gray-500 uppercase">{u.role} · {u.sessions} sesiones</p>
              </div>
              <p className="text-sm font-black text-[#CCFF00] font-['Barlow_Condensed']">{fmtDuration(u.total_seconds)}</p>
            </li>
          ))}
          {(data.top_users || []).length === 0 && <li className="text-center text-xs text-gray-500 py-4">Sin actividad reciente</li>}
        </ul>
      </section>

      {isAdmin && <ClubManagersDialog clubId={clubId} open={managersOpen} onClose={() => setManagersOpen(false)} />}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className={`p-3 rounded border ${accent ? "bg-[#CCFF00]/10 border-[#CCFF00]/40" : "bg-white/5 border-white/10"}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-400">
        <Icon size={14} weight="fill" className="text-[#CCFF00]" /> {label}
      </div>
      <p className={`text-2xl md:text-3xl font-black font-['Barlow_Condensed'] mt-1 ${accent ? "text-[#CCFF00]" : "text-white"}`}>{value}</p>
      {sub && <p className="text-[10px] text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

function ClubManagersDialog({ clubId, open, onClose }) {
  const [managers, setManagers] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("full");

  const load = () => api.get(`/clubs/${clubId}/managers`).then(({ data }) => setManagers(data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open, clubId]); // eslint-disable-line
  useEffect(() => {
    if (!open) return;
    const tm = setTimeout(() => api.get(`/clubs/${clubId}/managers/candidates?q=${encodeURIComponent(q)}`).then(({ data }) => setCandidates(data)).catch(() => {}), 250);
    return () => clearTimeout(tm);
  }, [q, open, clubId]);

  const active = new Set(managers.map((m) => m.user_id));
  const filtered = candidates.filter((c) => !active.has(c.id));
  const grant = async (uid) => { try { await api.post(`/clubs/${clubId}/managers`, { user_id: uid, permission_level: level }); toast.success(`Autorizado (${level})`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };
  const revoke = async (uid) => { try { await api.delete(`/clubs/${clubId}/managers/${uid}`); toast.success("Removido"); load(); } catch { toast.error("Error"); } };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-black border-white/10 text-white" data-testid="club-managers-dialog">
        <DialogHeader><DialogTitle className="text-white uppercase font-['Barlow_Condensed']"><ShieldStar size={18} className="inline mr-1 text-[#CCFF00]" /> Directivos autorizados del club</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Autorizados ({managers.length})</p>
            {managers.length === 0 ? <p className="text-sm italic text-gray-500">Sin autorizados. Solo admin ve este dashboard.</p> :
              <ul className="space-y-1.5">
                {managers.map((m) => (
                  <li key={m.user_id} className="flex items-center gap-2 p-2 border border-white/10 rounded bg-white/5" data-testid={`club-manager-${m.user_id}`}>
                    <UserAvatar src={m.user?.profile_photo_url} fallback={(m.user?.name || "?").slice(0,2)} className="w-7 h-7 rounded-full" />
                    <span className="flex-1 text-sm font-bold">{m.user?.name || m.user_id}</span>
                    <Badge className="text-[9px] uppercase" style={{ background: m.permission_level === "full" ? "#CCFF00" : "#8a8a8a", color: "#000" }}>{m.permission_level === "full" ? "Total" : "Solo lectura"}</Badge>
                    <Button size="sm" variant="outline" onClick={() => revoke(m.user_id)} className="text-red-400 h-7 text-[10px] border-red-400/40">Revocar</Button>
                  </li>
                ))}
              </ul>
            }
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Nivel a otorgar</p>
            <div className="flex gap-2 mb-2">
              <button type="button" onClick={() => setLevel("full")} className={`flex-1 py-2 rounded border-2 text-xs font-bold ${level === "full" ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-transparent text-white border-white/20"}`} data-testid="club-mgr-level-full">Total</button>
              <button type="button" onClick={() => setLevel("readonly")} className={`flex-1 py-2 rounded border-2 text-xs font-bold ${level === "readonly" ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-transparent text-white border-white/20"}`} data-testid="club-mgr-level-readonly">Solo lectura</button>
            </div>
            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded px-2">
              <MagnifyingGlass size={14} className="text-gray-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" className="bg-transparent border-0 text-white" data-testid="club-mgr-search" />
            </div>
            <ul className="mt-2 space-y-1.5 max-h-56 overflow-y-auto">
              {filtered.map((c) => (
                <li key={c.id} className="flex items-center gap-2 p-2 border border-white/10 rounded">
                  <UserAvatar src={c.profile_photo_url} fallback={(c.name || "?").slice(0,2)} className="w-7 h-7 rounded-full" />
                  <span className="flex-1 text-sm">{c.name} <span className="text-[10px] text-gray-500 uppercase">({c.role})</span></span>
                  <Button size="sm" onClick={() => grant(c.id)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] h-7 text-[10px]" data-testid={`club-mgr-add-${c.id}`}><UserPlus size={12} className="mr-1" />Autorizar</Button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
